import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimExportWorkOrderPricesQuery } from 'gql/graphql';
import { bimExportWorkOrderPricesGql } from 'pages/Project/BimProject/WorkOrder/bimWorkOrderQueries';
import { downloadURI } from 'utils/downloadDataURI';
import type { TActionCommonInterface } from 'utils/types';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

const requestId = 'bimExportWorkOrderPrices';

export const useWorkOrderPricesExport = ({ workOrderId }: { workOrderId: number }) => {
    const dispatch = useDispatch();

    const [exportWorkOrderPricesReset] = useResetStoreData({ requestId });

    const successHandler = useCallback(
        (successData: TActionCommonInterface<BimExportWorkOrderPricesQuery>) => {
            const { dataUrl, name } = successData?.payload?.bimExportWorkOrderPrices?.data || {};

            if (dataUrl && name) {
                downloadURI(dataUrl, name);
            }

            exportWorkOrderPricesReset(undefined);
        },
        [exportWorkOrderPricesReset],
    );

    const [
        bimExportWorkOrderPricesData,
        bimExportWorkOrderPricesStatus,
        bimExportWorkOrderPricesError,
        bimExportWorkOrderPricesRequest,
        ,
        bimExportWorkOrderPricesAbort,
    ] = useRequest({
        requestId,
        query: bimExportWorkOrderPricesGql,
        withEffect: false,
        dependenciesUpdater: successHandler,
    });

    const isLoadingBimExportWorkOrderPrices = useMemo(
        () => bimExportWorkOrderPricesStatus === RequestStatus.FETCHING,
        [bimExportWorkOrderPricesStatus],
    );

    const handleBimExportWorkOrderPrices = useCallback(() => {
        dispatch(bimExportWorkOrderPricesRequest({ workOrderId }));
    }, [bimExportWorkOrderPricesRequest, dispatch, workOrderId]);

    const exportWorkOrderPricesAbort = useCallback(() => {
        dispatch(bimExportWorkOrderPricesAbort(undefined));
    }, [bimExportWorkOrderPricesAbort, dispatch]);

    return {
        isLoadingBimExportWorkOrderPrices,
        bimExportWorkOrderPricesData: bimExportWorkOrderPricesData?.bimExportWorkOrderPrices,
        bimExportWorkOrderPricesStatus,
        bimExportWorkOrderPricesError,
        exportWorkOrderPricesAbort,
        handleBimExportWorkOrderPrices,
    };
};
